import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';

import { Platform } from '@angular/cdk/platform';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { MomentModule } from 'ngx-moment';
import { debounceTime, filter, map, mergeMap, tap } from 'rxjs/operators';
import { Item, Product } from '../../models';
import { Booking } from '../../models/booking';
import { BookingItem } from '../../models/booking-item';
import { DocketService, ProductService } from '../../shared';
import { ProductCategoryService } from '../../shared/services/product-category.service';
import { ConfigService } from '../services/config.service';
import { AssignItemsTableRowComponent } from './assign-items-table-row/assign-items-table-row.component';
import { MobileAssignItemsTableRowComponent } from './assign-items-table-row/mobile-assign-items-table-row.component';
@Component({
  selector: 'assign-items-table',
  templateUrl: './assign-items-table.component.html',
  providers: [DocketService, ProductService, ProductCategoryService],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MomentModule,
    AssignItemsTableRowComponent,
    MobileAssignItemsTableRowComponent
  ]
})
export class AssignItemsTableComponent implements OnInit {
  @Input() set booking(value: Booking) {
    this.booking$.next(value);
  }
  private booking$ = new BehaviorSubject<Booking>(null);
  alternativeProductItems: { [bookingId: number]: Product[] } = {};
  formGroup = new FormGroup({ items: new FormArray([]) });
  dateTimeFormat = ConfigService.dates.longDateTimeFormat;
  platform = inject(Platform);

  iosOrAndroid = signal(false);

  @Output() itemsFormSet = new EventEmitter<UntypedFormGroup>();
  @Output() itemFormArray = this.booking$.pipe(
    filter((booking) => Boolean(booking)),
    mergeMap((booking) => {
      return forkJoin(this.getProductsForEachItem(booking)).pipe(
        map((items) => items.flatMap((item) => item)),
        tap((productItemIds) => {
          this.alternativeProductItems = productItemIds.reduce(
            (acc, { bookingItemId, products }) => ({
              ...acc,
              [bookingItemId]: products
            }),
            {}
          );
        }),
        map((_) => {
          const formGroups = booking.items.flatMap((item) => {
            if (item.quantity === 1 || item.product.quantifiable) {
              const fg = this.buildDefaultFormGroup(item);
              fg.controls['quantity'].patchValue(item.quantity);
              return [fg];
            }
            return new Array(item.quantity)
              .fill(1)
              .map((q) => this.buildDefaultFormGroup(item));
          });
          while (this.formGroup.controls.items.length !== 0) {
            this.formGroup.controls.items.removeAt(0);
          }
          formGroups.forEach((group) =>
            this.formGroup.controls.items.push(group)
          );
          return this.formGroup;
        }),
        tap((fg) => {
          let i = 0;
          while (fg.get(`items.${i}.item`)) {
            const itemControl = this.formGroup.get(`items.${i}.item`);
            itemControl.markAsTouched();
            i++;
          }
        })
      );
    })
  );

  constructor(
    private categoryService: ProductCategoryService,
    private productService: ProductService
  ) {
    this.formGroup.valueChanges
      .pipe(debounceTime(200))
      .subscribe((value) => this.validateInputsForUniqueValues(value));
  }

  ngOnInit() {
    // iPads are not detected as iOS as they do not send correct user agent by default
    // Need to ask user to disable the "desktop website" in settings of safari or
    // in the address bar of safari for hirelogic
    this.iosOrAndroid.set(this.platform.IOS || this.platform.ANDROID);
  }

  private getProductsForEachItem(booking: Booking): Observable<
    {
      bookingItemId: number;
      products: Product[];
    }[]
  >[] {
    const productIds = booking.items
      .filter(({ product_id }) => !!product_id)
      .map(({ product_id, id }) => ({
        bookingItemId: id,
        product_id
      }));
    const categoryIds = booking.items
      .filter(({ product_id }) => !product_id)
      .map(({ category_id, id }) => category_id);

    const products = productIds.map(({ bookingItemId, product_id }) =>
      this.productService
        .getProduct(product_id, ['items'])
        .pipe(
          map((product) => [
            { bookingItemId, products: [product] as Product[] }
          ])
        )
    );
    const categoryProducts =
      categoryIds.length === 0
        ? of([])
        : this.categoryService
            .getProductCategoriesById(categoryIds, ['products.items'])
            .pipe(
              map((categories) => {
                return booking.items
                  .filter(({ product_id }) => !product_id)
                  .map((item) => {
                    return {
                      bookingItemId: item.id,
                      products:
                        categories.find(({ id }) => item.category_id)
                          ?.products ?? []
                    };
                  });
              })
            );
    return [...products, categoryProducts];
  }

  private buildDefaultFormGroup(item: BookingItem) {
    const bookedItem = item.item_id
      ? this.getItemFromAlternativeProducts(item.id, item.item_id)
      : null;
    return new FormGroup({
      bookingItem: new FormControl(item),
      item: new FormControl(bookedItem, this.availableValidator),
      quantity: new FormControl(1)
    });
  }

  private getItemFromAlternativeProducts(
    bookingItemId: number,
    itemId: number
  ): Item {
    return this.alternativeProductItems[bookingItemId]
      .flatMap(({ items }) => items)
      .find((item) => item.id === itemId);
  }

  validateInputsForUniqueValues(formValue: typeof this.formGroup.value) {
    const formValueCodes: string[] = formValue.items
      .map(({ item }) => item?.code as string | null)
      .filter(Boolean);
    const duplicateIndexes = formValueCodes.map((code, rowIndex) => {
      const duplicateRows = formValueCodes
        .map((ac, i) => (code === ac && i !== rowIndex ? i : null))
        .filter((duplicate) => duplicate !== null);
      return { rowIndex, duplicateRows };
    });
    duplicateIndexes.forEach(({ rowIndex, duplicateRows }) => {
      const rowItemControl = this.formGroup.get(`items.${rowIndex}.item`);
      if (duplicateRows.length > 0) {
        rowItemControl.setErrors({
          ...rowItemControl.errors,
          duplicate: 'This item is already assigned'
        });
      } else {
        if (!rowItemControl.errors?.duplicate && rowItemControl.errors) {
          rowItemControl.setErrors({
            ...rowItemControl.errors,
            duplicate: undefined
          });
        } else {
          rowItemControl.setErrors(null);
        }
      }
    });
  }

  validateFormArrayUniqueItemIds(formArray: FormArray, itemIdField: string) {
    const itemIds = formArray.controls.map(
      (control) => control.get(itemIdField).value
    );
    const duplicateIndexes = itemIds
      .map((item, index) => {
        return itemIds.filter((ac, i) => item === ac && i !== index).length > 0
          ? index
          : null;
      })
      .filter((index) => index !== null);

    formArray.controls.forEach((control, index) => {
      if (duplicateIndexes.includes(index)) {
        control
          .get(itemIdField)
          .setErrors({ duplicate: 'This item is already assigned' });
      } else {
        control.get(itemIdField).setErrors(null);
      }
    });
  }

  availableValidator(control: UntypedFormControl) {
    if (control.value && control.value.status !== 'Available') {
      return { unavailable: `This item is ${control.value.status}` };
    }
    return null;
  }
}
